import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus, Edit3, BarChart3, ShieldCheck, Copy, Eye, Send, Square,
  Archive, RotateCcw, Trash2,
} from 'lucide-react';
import surveyApi from '../surveyApi';
import { SURVEY_TEMPLATES } from '../templates';

const STATUS_PILL = {
  open: 'bg-emerald-900/40 text-emerald-300',
  draft: 'bg-slate-700 text-slate-300',
  closed: 'bg-rose-900/40 text-rose-300',
};

export default function SurveysAdminList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newTemplate, setNewTemplate] = useState('blank');
  const [showArchived, setShowArchived] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const refresh = async (archived = showArchived) => {
    try {
      setLoading(true);
      const rows = await surveyApi.adminListAll(archived);
      setItems(rows);
      setError('');
    } catch (e) {
      setError(e.message || 'Failed to load surveys');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(showArchived); /* eslint-disable-next-line */ }, [showArchived]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    try {
      const { id } = await surveyApi.adminCreate({ title: newTitle.trim() });
      const tpl = SURVEY_TEMPLATES.find((t) => t.key === newTemplate);
      if (tpl) {
        if (tpl.survey && Object.keys(tpl.survey).length) await surveyApi.adminUpdate(id, tpl.survey);
        if (tpl.questions && tpl.questions.length) await surveyApi.adminImportQuestions(id, tpl.questions, true);
      }
      navigate(`/tiles/ksp_training/admin/surveys/${id}`);
    } catch (err) {
      setError(err.message || 'Failed to create survey');
    }
  };

  const guard = async (id, fn) => {
    setBusyId(id);
    try { await fn(); setError(''); }
    catch (e) { setError(e.message || 'Action failed'); }
    finally { setBusyId(null); }
  };

  const handleClone = (id) => guard(id, async () => {
    const { id: newId } = await surveyApi.adminClone(id);
    navigate(`/tiles/ksp_training/admin/surveys/${newId}`);
  });
  const handlePublishToggle = (s) => guard(s.id, async () => {
    await surveyApi.adminUpdate(s.id, { status: s.status === 'open' ? 'closed' : 'open' });
    await refresh();
  });
  const handlePreview = (id) => window.open(`/tiles/ksp_training/survey/${id}?preview=1`, '_blank', 'noopener');
  const handleArchive = (id) => {
    if (!window.confirm('Archive this survey? Responses are kept and you can restore it later.')) return;
    guard(id, async () => { await surveyApi.adminDelete(id); await refresh(); });
  };
  const handleRestore = (id) => guard(id, async () => { await surveyApi.adminRestore(id); await refresh(); });
  const handleHardDelete = (s) => {
    if (!window.confirm(`Permanently delete "${s.title}" and ALL ${s.response_count} responses? This cannot be undone.`)) return;
    guard(s.id, async () => { await surveyApi.adminHardDelete(s.id); await refresh(); });
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <h1 className="text-2xl font-bold text-slate-100">Surveys</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowArchived((v) => !v)}
            className={`px-3 py-2 rounded text-sm flex items-center gap-2 ${
              showArchived ? 'bg-amber-600 text-white' : 'bg-slate-700 hover:bg-slate-600 text-slate-100'}`}>
            <Archive className="w-4 h-4" /> {showArchived ? 'Viewing archived' : 'Show archived'}
          </button>
          {!showArchived && (
            <button onClick={() => setCreating((v) => !v)}
              className="px-3 py-2 rounded bg-amber-600 hover:bg-amber-500 text-white flex items-center gap-2">
              <Plus className="w-4 h-4" /> New Survey
            </button>
          )}
        </div>
      </div>

      {creating && !showArchived && (
        <form onSubmit={handleCreate} className="mb-6 p-4 rounded-lg bg-slate-800 border border-slate-700 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs text-slate-400 mb-1">Title</label>
            <input autoFocus value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
              placeholder="e.g. Q3 Employee Pulse"
              className="w-full px-3 py-2 rounded bg-slate-900 border border-slate-700 text-slate-100" />
          </div>
          <div className="min-w-[220px]">
            <label className="block text-xs text-slate-400 mb-1">Start from</label>
            <select value={newTemplate} onChange={(e) => setNewTemplate(e.target.value)}
              className="w-full px-3 py-2 rounded bg-slate-900 border border-slate-700 text-slate-100">
              {SURVEY_TEMPLATES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
            <p className="text-xs text-slate-500 mt-1">
              {SURVEY_TEMPLATES.find((t) => t.key === newTemplate)?.description}
            </p>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="px-3 py-2 rounded bg-emerald-600 hover:bg-emerald-500 text-white">Create &amp; edit</button>
            <button type="button" onClick={() => { setCreating(false); setNewTitle(''); setNewTemplate('blank'); }}
              className="px-3 py-2 rounded bg-slate-700 hover:bg-slate-600 text-slate-100">Cancel</button>
          </div>
        </form>
      )}

      {error && <div className="mb-4 p-3 rounded bg-rose-900/40 border border-rose-700 text-rose-200">{error}</div>}

      {loading ? (
        <div className="text-slate-400">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-slate-400">{showArchived ? 'No archived surveys.' : 'No surveys yet. Create your first one.'}</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-700">
          <table className="w-full text-sm">
            <thead className="bg-slate-800 text-slate-300">
              <tr>
                <th className="text-left px-4 py-2">Title</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Questions</th>
                <th className="text-left px-4 py-2">Responses</th>
                <th className="text-left px-4 py-2">Audience</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} className="border-t border-slate-700 hover:bg-slate-800/60">
                  <td className="px-4 py-2 text-slate-100">
                    <div className="flex items-center gap-2">
                      {s.title}
                      {!!s.is_anonymous && <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" title="Anonymous" />}
                    </div>
                    {s.related_training_title && (
                      <div className="text-xs text-slate-500">Follow-up to: {s.related_training_title}</div>
                    )}
                    {(s.round_count > 1 || (s.languages || []).length > 1) && (
                      <div className="text-xs text-slate-500">
                        {s.round_count > 1 && <>Round {s.round_count}</>}
                        {s.round_count > 1 && (s.languages || []).length > 1 && ' · '}
                        {(s.languages || []).length > 1 && s.languages.map((l) => l.toUpperCase()).join(' / ')}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-0.5 text-xs rounded ${STATUS_PILL[s.status] || STATUS_PILL.draft}`}>
                      {s.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-slate-300">{s.question_count}</td>
                  <td className="px-4 py-2 text-slate-300">
                    {s.round_count > 1 ? (
                      <span title={`${s.current_round_response_count} this round · ${s.response_count} across all rounds`}>
                        {s.current_round_response_count} <span className="text-slate-500">/ {s.response_count} total</span>
                      </span>
                    ) : s.response_count}
                  </td>
                  <td className="px-4 py-2 text-slate-400">{s.audience_mode === 'roles' ? 'Targeted' : 'All'}</td>
                  <td className="px-4 py-2">
                    <div className="flex items-center justify-end gap-2.5">
                      {showArchived ? (
                        <>
                          <IconBtn onClick={() => handleRestore(s.id)} disabled={busyId === s.id} className="text-emerald-400 hover:text-emerald-300" label="Restore"><RotateCcw className="w-4 h-4" /></IconBtn>
                          <IconBtn onClick={() => handleHardDelete(s)} disabled={busyId === s.id} className="text-rose-400 hover:text-rose-300" label="Delete permanently"><Trash2 className="w-4 h-4" /></IconBtn>
                        </>
                      ) : (
                        <>
                          <IconBtn onClick={() => handlePreview(s.id)} className="text-slate-300 hover:text-white" label="Preview"><Eye className="w-4 h-4" /></IconBtn>
                          {s.status === 'open' ? (
                            <IconBtn onClick={() => handlePublishToggle(s)} disabled={busyId === s.id} className="text-amber-400 hover:text-amber-300" label="Close survey"><Square className="w-4 h-4" /></IconBtn>
                          ) : (
                            <IconBtn onClick={() => handlePublishToggle(s)} disabled={busyId === s.id || s.question_count === 0} className="text-emerald-400 hover:text-emerald-300" label={s.question_count === 0 ? 'Add a question before publishing' : 'Publish (open)'}><Send className="w-4 h-4" /></IconBtn>
                          )}
                          <Link to={`/tiles/ksp_training/admin/surveys/${s.id}/results`} className="text-sky-400 hover:text-sky-300" title="Results"><BarChart3 className="w-4 h-4" /></Link>
                          <Link to={`/tiles/ksp_training/admin/surveys/${s.id}`} className="text-amber-400 hover:text-amber-300" title="Edit"><Edit3 className="w-4 h-4" /></Link>
                          <IconBtn onClick={() => handleClone(s.id)} disabled={busyId === s.id} className="text-slate-300 hover:text-white" label="Duplicate"><Copy className="w-4 h-4" /></IconBtn>
                          <IconBtn onClick={() => handleArchive(s.id)} disabled={busyId === s.id} className="text-rose-400 hover:text-rose-300" label="Archive"><Archive className="w-4 h-4" /></IconBtn>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function IconBtn({ onClick, disabled, className, label, children }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={label} aria-label={label}
      className={`${className} disabled:opacity-30 disabled:cursor-not-allowed`}>
      {children}
    </button>
  );
}
